
import React from 'react';
import { Users } from 'lucide-react';
import AvatarPics from './AvatarPics';
import { cn } from "@/lib/utils";

interface MembersListProps {
  count: number;
  members?: any[]; // Add optional members array to display actual avatars
}

const MembersList = ({ count, members = [] }: MembersListProps) => {
  // If we have actual member data, display their avatars
  if (members && members.length > 0) {
    return (
      <div className="flex items-center">
        <div className="flex -space-x-4"> {/* Increased negative space */}
          {members.slice(0, 3).map((member, i) => {
            // Extract initials from first_name and last_name
            const initials = member.first_name && member.last_name 
              ? `${member.first_name[0]}${member.last_name[0]}` 
              : '';
              
            return (
              <div 
                key={member.id || i} 
                className="relative"
              >
                <AvatarPics 
                  profilePictureUrl={member.profile_picture_url} 
                  initials={initials}
                  className={cn(
                    "h-10 w-10 rounded-full", // Make avatars perfectly circular
                    "border-[2px] border-[#FFFFFF]", // Brand purple border
                    "shadow-md", // Add a subtle shadow for depth
                    "hover:scale-110 transition-transform" // Maintain hover effect
                  )} 
                />
              </div>
            );
          })}
          
          {/* If there are more members than we're showing */}
          {members.length > 3 && (
            <div className="h-10 w-10 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-xs text-gray-600 dark:text-gray-300 font-medium border-[3px] border-[#8B5CF6] shadow-md">
              +{members.length - 3}
            </div>
          )}
        </div>
        
        <div className="h-8 flex items-center pl-4">
          <Users size={16} className="text-muted-foreground" />
          <span className="text-xs text-muted-foreground ml-1">{count} members</span>
        </div>
      </div>
    );
  }
  
  // Fallback to numbered circles if no member data is provided
  return (
    <div className="flex -space-x-2">
      {Array(Math.min(count, 3)).fill(0).map((_, i) => (
        <div 
          key={i}
          className="h-8 w-8 rounded-full bg-gradient-to-br from-[#33C3F0] to-[#2b9cfd] dark:from-[#1e3fee] dark:to-[#283dae] flex items-center justify-center text-xs text-white font-medium border-2 border-background shadow-lg transform hover:scale-110 transition-transform"
          style={{ minWidth: '2rem' }}
        >
          {i + 1}
        </div>
      ))}
      
      {count > 3 && (
        <div className="h-8 w-8 rounded-full bg-gradient-to-br from-gray-400 to-gray-500 flex items-center justify-center text-xs text-white font-medium border-2 border-background shadow-lg">
          +{count - 3}
        </div>
      )}
      
      <div className="h-8 flex items-center pl-4">
        <Users size={16} className="text-muted-foreground" />
        <span className="text-xs text-muted-foreground ml-1">{count} members</span>
      </div>
    </div>
  );
};

export default MembersList;
